import { useCallback, useMemo, useRef, useState } from 'react'

import {
  Field,
  FieldDescription,
  FieldError,
  Label,
  Stack,
  Text
} from '@vtex/shoreline'
import debounce from 'lodash/debounce'

import SelectSearchable from '~/components/SelectSearchable'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { useInsertionOrdersInfiniteQuery } from '~/modules/retailMedia/hooks/useInsertionOrdersInfiniteQuery'

import {
  clampInsertionOrderQuery,
  filterExcludedInsertionOrders,
  LINK_INSERTION_ORDER_DEFAULT_EXCLUDED_STATUSES,
  toLinkInsertionOrderOption
} from './helpers'
import { LinkInsertionOrderOptionRow } from './Option'
import { LinkInsertionOrderPreview } from './Preview'

import styles from './styles.module.css'

const menuStyles = {
  menu: (base: Record<string, unknown>) => ({
    ...base,
    padding: 0,
    marginTop: 'var(--sl-space-1)',
    border: 'var(--sl-border-base)',
    borderRadius: 'var(--sl-radius-2)',
    boxShadow: 'var(--sl-shadow-1)',
    overflow: 'hidden',
    zIndex: 9999
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    padding: 0
  })
}

export const LinkInsertionOrder = ({
  value,
  onChange,
  advertiserId,
  status,
  excludedStatuses = LINK_INSERTION_ORDER_DEFAULT_EXCLUDED_STATUSES,
  excludedIds,
  autoFocus,
  required = true,
  disabled,
  error,
  className,
  'data-testid': dataTestId = 'link-insertion-order'
}: LinkInsertionOrderProps) => {
  const { t } = useAPPTranslation()
  const [search, setSearch] = useState('')
  const [preLoading, setPreLoading] = useState(false)

  const debouncedSetSearch = useRef(
    debounce((next: string) => {
      setSearch(next)
      setPreLoading(false)
    }, 500)
  ).current

  const handleQueryTerm = useCallback(
    (term: string | null) => {
      const next = clampInsertionOrderQuery(term ?? '')

      if (!next) {
        debouncedSetSearch.cancel()
        setSearch('')
        setPreLoading(false)
        return
      }

      setPreLoading(true)
      debouncedSetSearch(next)
    },
    [debouncedSetSearch]
  )

  const {
    items,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInsertionOrdersInfiniteQuery(
    {
      query: search || undefined,
      advertiser_id: advertiserId,
      status,
      quantity: 20
    },
    { enabled: !value && !disabled }
  )

  const options = useMemo(() => {
    const filtered = filterExcludedInsertionOrders(
      items,
      excludedStatuses,
      excludedIds
    )
    return filtered?.map(toLinkInsertionOrderOption) ?? []
  }, [excludedIds, excludedStatuses, items])

  const handleSelect = useCallback(
    (option: LinkInsertionOrderOption | null) => {
      onChange(option?.data ?? null)
    },
    [onChange]
  )

  const handleMenuScrollToBottom = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleChange = useCallback(() => {
    onChange(null)
    setSearch('')
  }, [onChange])

  const fieldError =
    error || (isError ? t('rm:campaign.insertionOrder.error') : undefined)

  const rootClassName = className
    ? `${styles.field} ${className}`
    : styles.field

  if (value) {
    return (
      <div className={rootClassName} data-testid={dataTestId}>
        <Stack className={styles.fieldInner} fluid>
          <LinkInsertionOrderPreview
            insertionOrder={value}
            onChange={handleChange}
            disabled={disabled}
          />
          {fieldError ? (
            <Text
              as="p"
              variant="caption2"
              color="var(--sl-fg-critical)"
              role="alert"
            >
              {fieldError}
            </Text>
          ) : null}
        </Stack>
      </div>
    )
  }

  return (
    <div className={rootClassName} data-testid={dataTestId}>
      <Field
        className={styles.fieldInner}
        error={Boolean(fieldError)}
        space="large"
      >
        <Label
          className={styles.label}
          htmlFor="link-insertion-order-select"
          optional={!required}
        >
          {t('rm:campaign.insertionOrder.label')}{' '}
          {required && <span className={styles.required}>*</span>}
        </Label>

        <SelectSearchable
          name="link-insertion-order-select"
          inputId="link-insertion-order-select"
          placeholder={t('rm:campaign.insertionOrder.placeholder')}
          options={options}
          components={{ Option: LinkInsertionOrderOptionRow }}
          loading={isLoading || preLoading || isFetchingNextPage}
          onChange={handleSelect}
          isClearable
          isSearchable
          isDisabled={disabled}
          autoFocus={autoFocus}
          onInputChange={handleQueryTerm}
          onMenuScrollToBottom={handleMenuScrollToBottom}
          filterOption={() => true}
          loadingMessage={() => `${t('c:words.loading')}...`}
          noOptionsMessage={() => t('rm:campaign.insertionOrder.empty')}
          styles={menuStyles}
        />

        <FieldDescription
          className={styles.description}
          data-testid="link-io-hint"
        >
          {t('rm:campaign.insertionOrder.hint')}
        </FieldDescription>

        <FieldError data-testid="link-io-error">{fieldError}</FieldError>
      </Field>
    </div>
  )
}
